import React from "react"
import { TruckFilled, QuestionOutlined } from "@ant-design/icons"

import { Tooltip } from "antd"

/**
 * Global application icon definitions
 *
 * This file centralizes all icon definitions used throughout the application.
 * Icons are organized by domain/category for easy maintenance.
 *
 * Each category should follow this pattern:
 * 1. Define the icons object with 'as const'
 * 2. Export a type for the icon keys
 * 3. Export a labels object mapping keys to display text
 * 4. Provide helper functions: getIcon, getLabel, renderIcon (with tooltip)
 */

// ============================================================================
// Generic Icon Helpers
// ============================================================================

/**
 * Generic function to get an icon from a map
 */
export const getIcon = <T extends string>(
	iconMap: Record<T, JSX.Element>,
	key: T,
	fallback?: JSX.Element
): JSX.Element => {
	return (
		iconMap[key] ||
		fallback || (
			<QuestionOutlined style={{ fontSize: "18px", color: "#8c8c8c" }} />
		)
	)
}

/**
 * Generic function to get a label from a map
 */
export const getLabel = <T extends string>(
	labelMap: Record<T, string>,
	key: T
): string => {
	return labelMap[key] || key
}

/**
 * Generic function to render an icon with a tooltip
 */
export const renderIconWithTooltip = (
	icon: JSX.Element,
	label: string,
	instantHide: boolean = true
): JSX.Element => {
	return (
		<Tooltip
			title={label}
			mouseEnterDelay={instantHide ? 0 : undefined}
			mouseLeaveDelay={instantHide ? 0 : undefined}
		>
			<span>{icon}</span>
		</Tooltip>
	)
}

// ============================================================================
// Custom SVG Icons
// ============================================================================

// SVG Bike icon
const BikeIcon: React.FC<{
	style?: React.CSSProperties
}> = ({ style }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		enableBackground="new 0 0 24 24"
		viewBox="2 5 20 14"
		style={style}
		fill={style?.color || "#4d4d4dff"}
	>
		<g>
			<rect fill="none" height="24" width="24" />
		</g>
		<g>
			<g>
				<path d="M19,7c0-1.1-0.9-2-2-2h-3v2h3v2.65L13.52,14H10V9H6c-2.21,0-4,1.79-4,4v3h2c0,1.66,1.34,3,3,3s3-1.34,3-3h4.48L19,10.35V7 z M7,17c-0.55,0-1-0.45-1-1h2C8,16.55,7.55,17,7,17z" />
				<rect height="2" width="5" x="5" y="6" />
				<path d="M19,13c-1.66,0-3,1.34-3,3s1.34,3,3,3s3-1.34,3-3S20.66,13,19,13z M19,17c-0.55,0-1-0.45-1-1s0.45-1,1-1s1,0.45,1,1 S19.55,17,19,17z" />
			</g>
		</g>
	</svg>
)

// SVG Warehouse icon
export const WarehouseIcon: React.FC<{
	style?: React.CSSProperties
}> = ({ style }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		style={style}
		fill={style?.color || "#4d4d4dff"}
	>
		<path d="M22 21V7L12 3 2 7v14h6v-8h8v8h6zm-8 0v-6h-4v6H8v-6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v6h-2z" />
	</svg>
)

// ============================================================================
// Vehicle Type Icons (used in routes, assignments, etc.)
// ============================================================================

export const VEHICLE_TYPE_ICONS = {
	van: <TruckFilled style={{ fontSize: "18px", color: "#818181ff" }} />,
	bike: (
		<BikeIcon style={{ width: "18px", height: "18px", color: "#818181ff" }} />
	),
	unrouted: (
		<QuestionOutlined style={{ fontSize: "18px", color: "#818181ff" }} />
	),
	fr: <QuestionOutlined style={{ fontSize: "18px", color: "#818181ff" }} />, // Alias for unrouted
} as const

export type VehicleType = keyof typeof VEHICLE_TYPE_ICONS

export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
	van: "Van",
	bike: "Bike",
	unrouted: "Unrouted",
	fr: "Unrouted", // Alias for unrouted
}

export const getVehicleTypeIcon = (type: VehicleType): JSX.Element => {
	return getIcon(VEHICLE_TYPE_ICONS, type)
}

export const getVehicleTypeLabel = (type: VehicleType): string => {
	return getLabel(VEHICLE_TYPE_LABELS, type)
}

export const renderVehicleTypeIcon = (type: VehicleType): JSX.Element => {
	const icon = getVehicleTypeIcon(type)
	const label = getVehicleTypeLabel(type)
	return renderIconWithTooltip(icon, label)
}

// ============================================================================
// Add more icon categories below as needed
// ============================================================================

// Example structure for future icon categories:
//
// export const ORDER_STATUS_ICONS = {
//   pending: <ClockCircleOutlined style={{ fontSize: "16px" }} />,
//   completed: <CheckCircleOutlined style={{ fontSize: "16px", color: "var(--success-color)" }} />,
//   cancelled: <CloseCircleOutlined style={{ fontSize: "16px", color: "var(--error-color)" }} />,
// } as const
//
// export type OrderStatus = keyof typeof ORDER_STATUS_ICONS
//
// export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
//   pending: "Pending",
//   completed: "Completed",
//   cancelled: "Cancelled",
// }
//
// export const getOrderStatusIcon = (status: OrderStatus): JSX.Element => {
//   return getIcon(ORDER_STATUS_ICONS, status)
// }
//
// export const getOrderStatusLabel = (status: OrderStatus): string => {
//   return getLabel(ORDER_STATUS_LABELS, status)
// }
//
// export const renderOrderStatusIcon = (status: OrderStatus): JSX.Element => {
//   const icon = getOrderStatusIcon(status)
//   const label = getOrderStatusLabel(status)
//   return renderIconWithTooltip(icon, label)
// }
